export interface AdminSession {
  authenticated: boolean;
  email?: string;
  csrfToken?: string;
}

export interface AISettings {
  enabled: boolean;
  primaryModel: string;
  fallbackModels: string[];
  temperature: number;
  maxTokens: number;
  tonePreset: 'friendly' | 'professional' | 'concise';
  groundingStrictness: number;
}

export interface BrandingSettings {
  logoMode: 'datocms' | 'url';
  logoUrl: string;
  wordmark: string;
}

export interface SecretStatus {
  openRouterConfigured: boolean;
  openRouterLast4?: string;
}

export interface ControlPanelSettings {
  ai: AISettings;
  branding: BrandingSettings;
  secrets: SecretStatus;
  updatedAt: string;
}

export interface AnalyticsSummary {
  conversations: number;
  fallbackRate: number;
  errorRate: number;
  topQuestions: string[];
}

const FALLBACK_SETTINGS: ControlPanelSettings = {
  ai: {
    enabled: true,
    primaryModel: 'deepseek/deepseek-chat',
    fallbackModels: ['deepseek/deepseek-r1-0528:free', 'meta-llama/llama-3.3-70b-instruct:free'],
    temperature: 0.7,
    maxTokens: 1000,
    tonePreset: 'friendly',
    groundingStrictness: 0.8,
  },
  branding: {
    logoMode: 'datocms',
    logoUrl: '',
    wordmark: 'Cyrus Portfolio',
  },
  secrets: {
    openRouterConfigured: false,
  },
  updatedAt: new Date(0).toISOString(),
};

const FALLBACK_ANALYTICS: AnalyticsSummary = {
  conversations: 0,
  fallbackRate: 0,
  errorRate: 0,
  topQuestions: [],
};

class AdminApiService {
  private readonly baseUrl = process.env.REACT_APP_ADMIN_API_BASE_URL || '/api/admin';
  private csrfToken = '';

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers || {}),
      },
      ...init,
    });

    if (!response.ok) {
      throw new Error(`Admin API error (${response.status})`);
    }

    return response.json() as Promise<T>;
  }

  async getSession(): Promise<AdminSession> {
    try {
      const session = await this.request<AdminSession>('/session');
      this.csrfToken = session.csrfToken || '';
      return session;
    } catch {
      return { authenticated: false };
    }
  }

  async login(password: string): Promise<AdminSession> {
    const session = await this.request<AdminSession>('/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
    this.csrfToken = session.csrfToken || '';
    return session;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/logout', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': this.csrfToken,
        },
      });
      this.csrfToken = '';
    } catch {
      // Keep UI resilient when backend is not available yet.
    }
  }

  async getSettings(): Promise<ControlPanelSettings> {
    try {
      return await this.request<ControlPanelSettings>('/settings');
    } catch {
      return FALLBACK_SETTINGS;
    }
  }

  async updateSettings(payload: Partial<ControlPanelSettings>): Promise<ControlPanelSettings> {
    return this.request<ControlPanelSettings>('/settings', {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': this.csrfToken,
      },
      body: JSON.stringify(payload),
    });
  }

  async getAnalyticsSummary(): Promise<AnalyticsSummary> {
    try {
      return await this.request<AnalyticsSummary>('/analytics/summary');
    } catch {
      return FALLBACK_ANALYTICS;
    }
  }
}

export const adminApiService = new AdminApiService();
